import { useState } from 'react'
import { PaymentMethodInput } from '@/models/payment-method'
import { useTranslation } from 'react-i18next'

export type PaymentMethodState = {
  cash: { checked: boolean; amount: string }
  creditCard: { checked: boolean; amount: string }
  other: { checked: boolean; amount: string; name: string }
}

export const initialPaymentMethods: PaymentMethodState = {
  cash: { checked: false, amount: '' },
  creditCard: { checked: false, amount: '' },
  other: { checked: false, amount: '', name: '' },
}

const parseAmount = (value: string): number | undefined => {
  const trimmed = value.trim()
  if (trimmed === '') return undefined
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined
}

export const toPaymentMethodInputs = (pm: PaymentMethodState): PaymentMethodInput[] => {
  return [
    pm.cash.checked && { method: 'cash' as const, amount: parseAmount(pm.cash.amount) },
    pm.creditCard.checked && {
      method: 'credit_card' as const,
      amount: parseAmount(pm.creditCard.amount),
    },
    pm.other.checked && {
      method: 'other' as const,
      otherName: pm.other.name,
      amount: parseAmount(pm.other.amount),
    },
  ].filter(Boolean) as PaymentMethodInput[]
}

/**
 * バリデーションエラーがあれば i18n キーを返し、なければ null を返す。
 * alert の呼び出しは呼び出し元のコンポーネントで行う。
 */
export const validatePaymentMethods = (pm: PaymentMethodState, totalAmount: number): string | null => {
  const checkedMethods = [pm.cash, pm.creditCard, pm.other].filter(m => m.checked)
  // その他が選択されているが、支払い方法名が入力されていない場合
  if (pm.other.checked && !pm.other.name.trim()) return 'ReceiptPayment.PleaseEnterPaymentMethodName'
  // 複数の支払い方法が選択されている場合、各支払い方法の金額が入力されているか、および合計金額が一致しているかを検証
  if (checkedMethods.length > 1) {
    const amounts = [
      pm.cash.checked ? parseAmount(pm.cash.amount) : 0,
      pm.creditCard.checked ? parseAmount(pm.creditCard.amount) : 0,
      pm.other.checked ? parseAmount(pm.other.amount) : 0,
    ]
    const hasInvalidAmount = amounts.some(a => a === undefined)
    if (hasInvalidAmount) return 'ReceiptPayment.PleaseEnterAmountForEachPaymentMethod'
    const total = (amounts as number[]).reduce((sum, a) => sum + a, 0)
    if (total !== totalAmount) return 'ReceiptPayment.TotalPaymentAmountDoesNotMatch'
  }
  return null
}

export const isMultipleChecked = (pm: PaymentMethodState): boolean => {
  return [pm.cash, pm.creditCard, pm.other].filter(m => m.checked).length > 1
}

export const usePaymentMethods = () => {
  const { t } = useTranslation()
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodState>({
    ...initialPaymentMethods,
    other: { ...initialPaymentMethods.other, name: t('ReceiptPayment.Other') },
  })

  const setField = <K extends keyof PaymentMethodState, F extends keyof PaymentMethodState[K]>(
    key: K,
    field: F,
    value: PaymentMethodState[K][F],
  ) => {
    setPaymentMethods(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }))
  }

  const isMultipleCheckedState = isMultipleChecked(paymentMethods)

  return { paymentMethods, setField, isMultipleChecked: isMultipleCheckedState }
}
